import React, { useReducer, useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import {
    UserInputs,
    LocationsDropdownWrapper,
    consultSchema,
    reducer,
    NearbyLocations,
    ShowAddressOrLink,
    initialFormData,
    initialUserData,
    FORM_ACTIONS,
    FORM_PROGRESS,
    CONSULT_FORM_SOURCE,
} from '.';
import {
    NETLIFY_FUNCTIONS_PATH,
    getFacebookCookies,
    getOptimizelyData,
    validateWithPossibleNow,
    syncPardotTracking,
    getLeadsCaptureEcho,
    isLeadsCaptureResponse,
} from '../../../utils';
import axios from 'axios';
import {
    FormSuccess,
    FormFailed,
    FormSubmitting,
    FORM_SUBMITTING_MESSAGES,
    AttributionSurvey,
    ConsentMsg,
    SmsOptInCheckbox,
    SMS_CONSENT_LEGAL_TERMS,
} from '../shared';
import { useScrollToAnchor } from '../../../hooks/useScrollToAnchor.ts';
import { useCloudampDataRef, getCloudampData } from '../../../hooks/useCloudampData';

export function MultipleStores({ siteData }) {
    const [formState, dispatch] = useReducer(reducer, {
        ...initialFormData,
        include: { formAction: FORM_ACTIONS.selfSchedule, formProgress: FORM_PROGRESS.selectLocation },
    });
    const isBrowser = typeof window !== 'undefined';
    // Keeps the hidden cloudamp input below populated and mirrored to localStorage.
    const cloudampDataRef = useCloudampDataRef();

    const [showAttributionSurvey, setShowAttributionSurvey] = useState(false);
    // State to hold form values for use in attribution survey
    const [formValues, setFormValues] = useState(null);
    const [smsOptIn, setSmsOptIn] = useState(false);
    const [locationError, setLocationError] = useState(false);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const { scrollToAnchor } = useScrollToAnchor({ offset: 300 });
    const apiEndpoint = `${NETLIFY_FUNCTIONS_PATH}consult-form-handler`;

    // Clear location error when a location is selected
    useEffect(() => {
        if (formState.store.salesforceValue) {
            setLocationError(false);
        }
    }, [formState.store.salesforceValue]);

    // Handle form submission
    const handleSubmit = async (values, helpers) => {
        if (!formState.store.salesforceValue) {
            setLocationError(true);
            return;
        }
        if (formState.include.formAction === FORM_ACTIONS.selfSchedule) {
            setIsRedirecting(true);
        }
        scrollToAnchor('consult-form');
        helpers.setSubmitting(true);
        const { fbp, fbc } = getFacebookCookies();
        const { experiment_exposures, experiment_user_ID } = getOptimizelyData();
        try {
            // Validate phone and email with PossibleNow
            const { phoneFound, wireless, emailFound, result, flags } = await validateWithPossibleNow(
                values.phone,
                values.email
            );

            const formData = {
                ...values,
                city: formState.metro.name,
                state: formState.store.stateAbbrev,
                location: formState.store.salesforceValue,
                consultType: formState.include.formAction,
                formSource: CONSULT_FORM_SOURCE,
                updates: true,
                // Checked => the user opted IN to marketing SMS, so the opt-OUT flag is false.
                marketing_sms_optout: !smsOptIn,
                cloudamp__data__c: getCloudampData(cloudampDataRef),
                fbp,
                fbc,
                experiment_exposures,
                experiment_user_ID,
                phoneFound,
                wireless,
                emailFound,
                result,
                flags,
            };

            // Submit to Netlify function
            const response = await axios.post(apiEndpoint, formData);

            // formData (not values) is used here because location is merged into formData from
            // formState.store.salesforceValue and is not present on the raw Formik values object.
            // Skipped on the leads-capture path: the lead is already in Salesforce, and syncing a
            // Pardot prospect on top of it would double-write.
            if (!isLeadsCaptureResponse(response)) syncPardotTracking(formData);

            // If backend returned a Self-Schedule URL, perform a top-level redirect
            if (isBrowser && formState.include.formAction === FORM_ACTIONS.selfSchedule && response.data.redirectUrl) {
                helpers.setSubmitting(false);
                helpers.setStatus('Success');
                helpers.resetForm();
                setSmsOptIn(false);
                dispatch({
                    type: 'formAction',
                    formAction: FORM_ACTIONS.selfSchedule,
                    formProgress: FORM_PROGRESS.selectLocation,
                    user: initialUserData,
                });
                setIsRedirecting(false);
                window.location.href = response.data.redirectUrl;
                return;
            } else {
                setIsRedirecting(false);
                // Store form values for use in attribution survey - only for Ask Question action.
                // The echo carries the ids the handler returned so the survey can reference the lead.
                setFormValues({ formType: 'question', ...formData, ...getLeadsCaptureEcho(response) });
                helpers.setSubmitting(false);
                helpers.setStatus('Success');
                setShowAttributionSurvey(true);
                helpers.resetForm();
                // smsOptIn lives outside Formik, so resetForm() won't clear it. Reset it explicitly so
                // the express-consent box never stays checked for the next submission on a shared device.
                setSmsOptIn(false);
                dispatch({
                    type: 'formAction',
                    formAction: FORM_ACTIONS.selfSchedule,
                    formProgress: FORM_PROGRESS.selectLocation,
                    user: initialUserData,
                });
            }
        } catch (error) {
            console.error('Form submission error:', error);
            helpers.setSubmitting(false);
            helpers.setStatus('Failed');
            setIsRedirecting(false);
            setFormValues(null);

            setTimeout(() => {
                helpers.resetForm();
                setSmsOptIn(false);
                dispatch({
                    type: 'formAction',
                    formAction: FORM_ACTIONS.selfSchedule,
                    formProgress: FORM_PROGRESS.selectLocation,
                    user: initialUserData,
                });
                setShowAttributionSurvey(false);
            }, 5000);
        }
    };

    const handleAttributionComplete = () => {
        setShowAttributionSurvey(false);
        setTimeout(() => {
            setFormValues(null);
        }, 10000); // Clear form values after 10 second
    };

    return (
        <div id="consult-form" className="hr-divider hr-divider-consult">
            <h2 className="text-center subheadsm">Book Your Free Consult</h2>
            <Formik
                initialValues={initialUserData}
                validationSchema={consultSchema}
                onSubmit={handleSubmit}
                validateOnChange={false}
            >
                {
                    // This way we can get access to all formik props
                    (formik) => {
                        return (
                            <Form className="container" method="POST">
                                <input ref={cloudampDataRef} type="hidden" name="cloudamp__data__c" />

                                {(formik.isSubmitting || isRedirecting) && (
                                    <FormSubmitting
                                        message={isRedirecting ? FORM_SUBMITTING_MESSAGES.selfSchedule : undefined}
                                    />
                                )}
                                {!isRedirecting && formik.status === 'Failed' && <FormFailed />}
                                {/* Only for Ask Question Action */}
                                {formValues && !formik.isSubmitting && !isRedirecting && formik.status !== 'Failed' && (
                                    <>
                                        {showAttributionSurvey ? (
                                            <div className="row justify-content-center">
                                                <div className="col-12 col-xl-10">
                                                    <AttributionSurvey
                                                        formValues={formValues}
                                                        onComplete={handleAttributionComplete}
                                                        isFullWidth={false}
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <FormSuccess />
                                        )}
                                    </>
                                )}

                                {/* Original form content - only shown when not in special states */}
                                {!formik.isSubmitting &&
                                    !isRedirecting &&
                                    formik.status !== 'Failed' &&
                                    !formValues && (
                                        <>
                                            <UserInputs
                                                isQuestion={formState.include.formAction === FORM_ACTIONS.question}
                                            />
                                            <div className="row locations-container">
                                                <div className="col locations-wrapper toggle">
                                                    <NearbyLocations
                                                        currentMetro={siteData.currentMetro}
                                                        dispatch={dispatch}
                                                    />
                                                    <LocationsDropdownWrapper
                                                        {...{
                                                            formState,
                                                            dispatch,
                                                            siteData,
                                                            locationError,
                                                        }}
                                                    />
                                                    <div className="row justify-content-center selected-location-container mb-3">
                                                        <div
                                                            className={`col-xl-10 selected-location-wrapper ${
                                                                formState.store.salesforceValue &&
                                                                formState.store.salesforceValue !== ''
                                                                    ? 'toggle'
                                                                    : ''
                                                            }`}
                                                        >
                                                            <div className="mb-0 text-center">
                                                                {(formState.store.open ||
                                                                    formState.include.formAction ===
                                                                        FORM_ACTIONS.question) && (
                                                                    <ShowAddressOrLink
                                                                        formState={formState}
                                                                        currentMetro={siteData.currentMetro}
                                                                    />
                                                                )}
                                                                {!formState.store.open &&
                                                                    formState.include.formAction !==
                                                                        FORM_ACTIONS.question && (
                                                                        <div className="d-flex justify-content-center mb-3">
                                                                            <div
                                                                                className="alert alert-warning col text-center p-2 mb-0"
                                                                                role="alert"
                                                                            >
                                                                                Selected location is not open yet, but
                                                                                you can still
                                                                                <span
                                                                                    onClick={() =>
                                                                                        dispatch({
                                                                                            type: 'formAction',
                                                                                            formAction:
                                                                                                FORM_ACTIONS.question,
                                                                                            formProgress:
                                                                                                FORM_PROGRESS.selectLocation,
                                                                                            user: formik.values,
                                                                                        })
                                                                                    }
                                                                                >
                                                                                    &nbsp;submit a question
                                                                                </span>
                                                                                ! Or select a different location.
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="row justify-content-center mb-3">
                                                <div className="col-xl-10">
                                                    <SmsOptInCheckbox
                                                        checked={smsOptIn}
                                                        onChange={(e) => setSmsOptIn(e.target.checked)}
                                                    />
                                                </div>
                                            </div>

                                            <div id="actions-btns" className="row gx-1 gy-4 justify-content-center">
                                                <div className="col-sm-5 col-lg-4 col-xl-3 text-center">
                                                    {
                                                        // Ask A Question — always visible unless in question mode
                                                        formState.include.formAction !== FORM_ACTIONS.question && (
                                                            <button
                                                                type="button"
                                                                className="milan-btn milan-navy-btn w-100"
                                                                onClick={() =>
                                                                    dispatch({
                                                                        type: 'formAction',
                                                                        formAction: FORM_ACTIONS.question,
                                                                        formProgress: FORM_PROGRESS.selectLocation,
                                                                        user: formik.values,
                                                                    })
                                                                }
                                                            >
                                                                Ask A Question <sup>*</sup>
                                                            </button>
                                                        )
                                                    }
                                                    {
                                                        // In question mode — Send Question
                                                        formState.include.formAction === FORM_ACTIONS.question && (
                                                            <button
                                                                className="milan-btn milan-navy-btn w-100"
                                                                type="submit"
                                                                disabled={!formik.isValid || locationError}
                                                            >
                                                                {formik.isSubmitting
                                                                    ? 'Submitting...'
                                                                    : 'Send Question'}
                                                            </button>
                                                        )
                                                    }
                                                </div>

                                                <div className="d-none d-sm-block col-sm-2 col-lg-1 text-center">
                                                    <div className="my-3 my-md-2">
                                                        <strong>OR</strong>
                                                    </div>
                                                </div>

                                                <div className="col-sm-5 col-lg-4 col-xl-3 text-center">
                                                    {
                                                        // See Available Times — always visible unless in question mode
                                                        formState.include.formAction !== FORM_ACTIONS.question && (
                                                            <button
                                                                id="local-schedule-btn"
                                                                type="submit"
                                                                className="milan-btn milan-red-btn w-100"
                                                                disabled={
                                                                    !formik.isValid ||
                                                                    locationError ||
                                                                    formState.store?.open === false // DO NOT do !formState.store.open because it can be null/undefined before selection, and we only want to disable if it's explicitly false
                                                                }
                                                            >
                                                                {formik.isSubmitting ? (
                                                                    'Submitting...'
                                                                ) : (
                                                                    <span>
                                                                        See Available Times <sup>**</sup>
                                                                    </span>
                                                                )}
                                                            </button>
                                                        )
                                                    }
                                                    {
                                                        // In question mode — Go Back
                                                        formState.include.formAction === FORM_ACTIONS.question && (
                                                            <button
                                                                type="button"
                                                                className="milan-btn milan-fallback-btn w-100"
                                                                onClick={() =>
                                                                    dispatch({
                                                                        type: 'formAction',
                                                                        formAction: FORM_ACTIONS.selfSchedule,
                                                                        formProgress: FORM_PROGRESS.selectLocation,
                                                                        user: formik.values,
                                                                    })
                                                                }
                                                            >
                                                                Go Back
                                                            </button>
                                                        )
                                                    }
                                                </div>
                                            </div>

                                            <div className="row justify-content-center mt-3">
                                                <div className="col-xl-10 lh-1">
                                                    {/* separator '. ' closes the Terms link before SMS_CONSENT_LEGAL_TERMS opens a new sentence */}
                                                    <ConsentMsg
                                                        cta={
                                                            // Names the button the user actually clicks: entering
                                                            // question mode swaps the CTA to "Send Question".
                                                            formState.include.formAction === FORM_ACTIONS.question
                                                                ? 'Send Question'
                                                                : 'Ask A Question<sup>*</sup> or See Available Times<sup>**</sup>'
                                                        }
                                                        legalTerms={SMS_CONSENT_LEGAL_TERMS}
                                                        separator=". "
                                                        termsLabel="Terms"
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}
                            </Form>
                        );
                    }
                }
            </Formik>
        </div>
    );
}

export default MultipleStores;
